import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { BarChart3, FileText, LifeBuoy } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicRoute from "./routes/PublicRoutes";
import KindRoute from "./routes/KindRoute";
import DashboardLayout from "./layouts/DashboardLayout";

import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ForgotUsername from "./pages/auth/ForgotUsername";
import Dashboard from "./pages/Dashboard";
import EmployeesList from "./pages/employees/EmployeesList";
import EmployeeDetail from "./pages/employees/EmployeeDetail";
import EmployeeTree from "./pages/employees/EmployeeTree";
import BranchesList from "./pages/branches/BranchesList";
import BranchDetail from "./pages/branches/BranchDetail";
import ComplaintsList from "./pages/complaints/ComplaintsList";
import ComplaintDetail from "./pages/complaints/ComplaintDetail";
import TodosList from "./pages/todos/TodosList";
import TodoStats from "./pages/todos/TodoStats";
import AttendanceList from "./pages/attendance/AttendanceList";
import PayrollList from "./pages/payroll/PayrollList";
import SalariesList from "./pages/payroll/SalariesList";
import PayrollMoneyView from "./pages/payroll/PayrollMoneyView";
import InventoryList from "./pages/inventory/InventoryList";
import InventoryEntries from "./pages/inventory/InventoryEntries";
import InventoryAdjustPage from "./pages/inventory/InventoryAdjustPage";
import MovieKeysList from "./pages/cinema/MovieKeysList";
import MovieKeyHistory from "./pages/cinema/MovieKeyHistory";
import MovieShows from "./pages/cinema/MovieShows";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Placeholder from "./pages/Placeholder";
import NotFound from "./pages/NotFound";
import { Toaster } from "react-hot-toast";

function AppRoutes() {
  const { partner, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-emerald-200 border-t-emerald-500" />
          <p className="text-body-sm text-gray-500">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/forgot-username" element={<ForgotUsername />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/employees" element={<EmployeesList />} />
          <Route path="/employees/:id" element={<EmployeeDetail />} />

          {/* Organization tree — both tiers. A branch sees its own reporting
              hierarchy; a chain picks any of its branches (or its head office)
              from the selector on the page. */}
          <Route path="/organization" element={<EmployeeTree />} />

          {/* Complaints - Full access for both CHAIN and BRANCH */}
          <Route path="/complaints" element={<ComplaintsList />} />
          <Route path="/complaints/:id" element={<ComplaintDetail />} />

          {/* Tasks, attendance and payroll are org-scoped from the token, so
              both tiers reach them; a chain additionally gets a branch filter
              on the page itself. */}
          <Route path="/todos" element={<TodosList />} />
          <Route path="/todos/stats" element={<TodoStats />} />
          <Route path="/attendance" element={<AttendanceList />} />
          <Route path="/payroll" element={<PayrollList />} />
          <Route path="/payroll/salaries" element={<SalariesList />} />
          <Route path="/payroll/history" element={<PayrollMoneyView />} />

          {/* Inventory — both tiers; chain gets a branch filter in-page */}
          <Route path="/inventory" element={<InventoryList />} />
          <Route path="/inventory/entries" element={<InventoryEntries />} />
          <Route path="/inventory/adjust" element={<InventoryAdjustPage />} />

          {/* Cinema / Movie Keys — §14; both tiers */}
          <Route path="/cinema" element={<MovieKeysList />} />
          <Route path="/cinema/history" element={<MovieKeyHistory />} />
          <Route path="/cinema/shows" element={<MovieShows />} />


          {/* Branches — CHAIN only */}
          <Route element={<KindRoute allow="CHAIN" />}>
            <Route path="/branches" element={<BranchesList />} />
            <Route path="/branches/:id" element={<BranchDetail />} />
          </Route>

          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route
            path="/analytics"
            element={
              <Placeholder
                title="Analytics"
                subtitle="Deeper insight into your organization's performance."
                icon={BarChart3}
              />
            }
          />
          <Route
            path="/reports"
            element={
              <Placeholder
                title="Reports"
                subtitle="Export and schedule reports for your organization."
                icon={FileText}
              />
            }
          />
          <Route
            path="/support"
            element={
              <Placeholder
                title="Support"
                subtitle="Reach the Alfennzo team for help with your account."
                icon={LifeBuoy}
              />
            }
          />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Main App component
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '12px',
              padding: '16px',
            },
            success: {
              style: {
                background: '#10b981',
                color: '#fff',
              },
            },
            error: {
              style: {
                background: '#ef4444',
                color: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}